import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown, { type Components } from "react-markdown";
import {
  Send,
  X,
  Lock,
  Sparkles,
  ExternalLink,
  MapPin,
  ArrowRight,
  PanelRightOpen,
  PanelRightClose,
  PenSquare,
  Pin,
  History,
  Trash2,
  ListChecks,
  ThumbsUp,
  ThumbsDown,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import { timeAgo } from "@/lib/notifications";
import { VoiceSearch } from "@/components/VoiceSearch";
import { NivaAvatar, NivaAvatarBadge } from "@/components/NivaAvatar";
import { NivaIntro } from "@/components/NivaIntro";
import { orgScopedPath } from "@/lib/orgDoor";
import { canUseNiva, hasOperatorAccess, isFreeBuyerUser, isNivaTrialExpired, isPortalAdmin, isStaffUser, nivaTrialDaysLeft, upgradeCtaPath } from "@/lib/access";
import { TalkToDataResult } from "@/components/TalkToDataResult";
import { cn } from "@/lib/utils";
import { useNiva } from "@/contexts/NivaContext";
import { useDraggableLauncher } from "@/hooks/useDraggableLauncher";

/** The assistant's name everywhere in the UI. The backend introduces itself by
 *  the same name (app/services/chatbot.py) — change both together, or she
 *  answers "what is your name?" with something the header contradicts. */
const ASSISTANT_NAME = "Niva";
/** The product name that sits under hers — keep it in step with the plan
 *  pages and the super admin switch, which sell the same feature by name. */
const ASSISTANT_TITLE = "BL AI Deal Assistant";

/** Conversations live in sessionStorage, not state alone: the panel survives
 *  route changes (ChatbotGate keys it on user.id) but a refresh used to wipe a
 *  long search thread with no way back. Per-tab and per-user, so a shared
 *  machine never restores someone else's chat. */
const CHAT_KEY = "bl.assistant.thread";
/** Keep the stored thread small — listing cards are the bulky part. */
const MAX_STORED_TURNS = 30;

function loadThread(userId: string): ChatTurn[] {
  try {
    const raw = sessionStorage.getItem(`${CHAT_KEY}.${userId}`);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? (parsed as ChatTurn[]) : [];
  } catch {
    return [];
  }
}

function saveThread(userId: string, turns: ChatTurn[]): void {
  try {
    const key = `${CHAT_KEY}.${userId}`;
    if (turns.length === 0) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, JSON.stringify(turns.slice(-MAX_STORED_TURNS)));
  } catch {
    /* private mode or quota — the thread just won't survive a refresh */
  }
}

/** Whether the viewer has dismissed the floating launcher (TKT-00119).
 *
 *  localStorage, not state or a session key: "get out of my way" that expires
 *  on the next page load isn't an answer to the complaint. Dismissing costs
 *  nothing, because the top bar carries an "Ask Niva" button on every shell at
 *  every width (components/Navigation.tsx has both a desktop pill and a mobile
 *  icon), so she is always one tap away — and the drawer header offers the
 *  floating pill back for anyone who wants it. */
const LAUNCHER_HIDDEN_KEY = "bl.assistant.launcher.hidden";

function loadLauncherHidden(): boolean {
  try {
    return localStorage.getItem(LAUNCHER_HIDDEN_KEY) === "1";
  } catch {
    return false;
  }
}

function saveLauncherHidden(hidden: boolean): void {
  try {
    if (hidden) localStorage.setItem(LAUNCHER_HIDDEN_KEY, "1");
    else localStorage.removeItem(LAUNCHER_HIDDEN_KEY);
  } catch {
    /* private mode — she just comes back on the next load */
  }
}

/** Seed prompts for the empty state — a blank box gives no clue what to ask. */
const SUGGESTIONS = [
  "Profitable auto shops in Texas under $500k",
  "HVAC businesses with $1M+ revenue",
  "Restaurants for sale in Florida",
];

// The model reliably falls back to markdown formatting (**bold**, "- " lists)
// even when told not to — LLMs default to it, and prompt instructions alone
// weren't reliable for this elsewhere in this component either. Rendering it
// properly, rather than fighting to suppress it, is what every other AI chat
// UI does. Overrides here keep it compact for a narrow chat bubble (default
// react-markdown spacing is sized for full-width articles) and safe (no raw
// HTML passthrough — react-markdown doesn't render embedded HTML by default).
const MARKDOWN_COMPONENTS: Components = {
  p: ({ children }) => <p className="mb-1.5 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="mb-1.5 last:mb-0 pl-4 list-disc space-y-0.5">{children}</ul>,
  ol: ({ children }) => <ol className="mb-1.5 last:mb-0 pl-4 list-decimal space-y-0.5">{children}</ol>,
  li: ({ children }) => <li>{children}</li>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  a: ({ children, href }) => <ChatLink href={href}>{children}</ChatLink>,
};

// Inline style, not Tailwind classes: the unlayered global `a { text-decoration:
// none; color: inherit }` in index.css beats layered utilities, which made links
// render as plain text.
const LINK_STYLE = { color: "var(--color-primary)", textDecoration: "underline", textUnderlineOffset: "2px" };

/** Links to the app's own pages (resource citations) navigate in place, in the
 * main area, under the viewer's org prefix. Anything else opens a new tab. */
function ChatLink({ href, children }: { href?: string; children?: React.ReactNode }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  let internalPath: string | null = null;
  try {
    const url = new URL(href ?? "", window.location.origin);
    if (url.origin === window.location.origin) internalPath = url.pathname + url.search + url.hash;
  } catch {
    internalPath = null;
  }

  if (internalPath === null) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" style={LINK_STYLE}>
        {children}
      </a>
    );
  }
  const target = orgScopedPath(internalPath, user?.org_slug);
  return (
    <a
      href={target}
      style={LINK_STYLE}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return; // let the browser open a new tab
        e.preventDefault();
        navigate(target);
      }}
    >
      {children}
    </a>
  );
}

interface ChatListingRef {
  id: string;
  title: string;
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  // Only ever set on assistant turns — echoed back on the next request so
  // the (stateless) backend can resolve a follow-up like "the second one"
  // against the ids it actually returned, not just the prose text.
  listing_refs?: ChatListingRef[];
}

interface ChatListingCard {
  id: string;
  title: string;
  reference_code: string | null;
  asking_price: number | null;
  revenue: number | null;
  ebitda: number | null;
  city_name: string | null;
  state_name: string | null;
  country_name: string | null;
  industry_category_id: number | null;
  is_sba_approved: boolean;
  is_locked_by_other: boolean;
  is_unlocked: boolean;
  has_member_fields: boolean;
}

interface ChatResponse {
  reply: string;
  listings: ChatListingCard[];
  requires_subscription?: boolean;
  // Talk to Your Data only (admin@portal.com) — always absent/null for every
  // other user's response, see app/schemas/chatbot.py::ChatResponse.
  sql?: string | null;
  dashboard?: TalkToDataDashboard | null;
  intent?: string | null;
  session_id?: string | null;
  // The saved chat_messages row for this reply — null when the turn wasn't
  // a Talk-to-Data turn or its save failed. What feedback rates (see
  // MessageFeedback/rateTurn below).
  message_id?: string | null;
}

export interface TalkToDataDashboard {
  chart_type: "bar" | "line" | "pie" | "area" | "scatter" | "table" | "metric_card";
  title: string;
  columns: string[];
  data: Record<string, unknown>[];
  description?: string;
}

interface ChatSessionSummary {
  id: string;
  title: string;
  updated_at: string;
}

interface PromptLibraryItem {
  id: string;
  question: string;
}

// One turn as rendered in the transcript — an assistant reply carries the
// listing cards returned alongside it, if any.
interface ChatTurn extends ChatMessage {
  listings?: ChatListingCard[];
  requiresSubscription?: boolean;
  sql?: string | null;
  dashboard?: TalkToDataDashboard | null;
  // The saved chat_messages id backing this turn — what feedback rates (see
  // MessageFeedback/rateTurn below). Absent/null means this turn was never
  // persisted (not a Talk-to-Data turn, or its save failed), in which case
  // no feedback UI is offered on it at all.
  messageId?: string | null;
  // Thumbs up/down on this assistant turn, once given — undefined/null means
  // not yet rated. Persisted in sessionStorage along with the rest of the
  // turn (saveThread/loadThread), so a rating survives a refresh within the
  // same tab. Locked once set (see MessageFeedback below): there is no
  // "change your rating" flow in v1, only give one.
  feedbackRating?: "up" | "down" | null;
}

function formatMoney(value: number | null): string | null {
  if (value === null) return null;
  return `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function ListingCardView({
  card,
  onOpen,
}: {
  card: ChatListingCard;
  onOpen: (card: ChatListingCard) => void;
}) {
  const location = [card.city_name, card.state_name].filter(Boolean).join(", ");
  // A 0 here means "not disclosed", not "zero revenue" — rendering it as "$0"
  // reads as a real figure and made cards look broken (see BL-000425).
  const stats = [
    card.asking_price ? { label: "asking", value: formatMoney(card.asking_price) } : null,
    card.revenue ? { label: "revenue", value: formatMoney(card.revenue) } : null,
  ].filter(Boolean) as { label: string; value: string }[];

  return (
    // A button, NOT a <Link>. A raw link to /listings/:id skipped the entire
    // entitlement path that Home.tsx's handleViewDetails enforces before
    // opening a listing (plan check, another buyer's exclusivity lock, and —
    // most importantly — spending one of a limited tier's monthly unlocks).
    // Going through onOpen keeps the chatbot on the same rules as every other
    // way of opening a listing instead of quietly becoming a free side door.
    <button
      type="button"
      onClick={() => onOpen(card)}
      className="group block w-full cursor-pointer rounded-xl border border-border/70 bg-card/80 p-3.5 text-left transition-colors hover:border-primary/50 hover:bg-card"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold leading-snug text-foreground">{card.title}</p>
        {card.is_locked_by_other && (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-700">
            <Lock className="h-3 w-3" /> Under exclusivity
          </span>
        )}
      </div>
      {location && (
        <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
          <MapPin className="h-3 w-3 shrink-0 text-primary/70" />
          {location}
        </p>
      )}
      {stats.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {stats.map((stat) => (
            <span
              key={stat.label}
              className="rounded-lg bg-muted px-2 py-1 text-[11px] text-muted-foreground"
            >
              <span className="font-semibold text-foreground">{stat.value}</span> {stat.label}
            </span>
          ))}
        </div>
      )}
      {!card.has_member_fields && (
        <p className="mt-2 text-[11px] italic text-muted-foreground">
          Some details require an upgrade{card.is_locked_by_other ? " or the lock to clear" : ""}.
        </p>
      )}
      <div className="mt-2.5 flex items-center justify-between gap-2 border-t border-border/50 pt-2">
        {card.reference_code && (
          <span className="text-[11px] font-medium text-muted-foreground">{card.reference_code}</span>
        )}
        <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-primary group-hover:underline">
          View listing <ExternalLink className="h-3 w-3" />
        </span>
      </div>
    </button>
  );
}

/**
 * Thumbs up/down under one Talk-to-Your-Data reply (only ever rendered for
 * those — see the turn.sql/turn.dashboard gate at the call site, not for
 * Niva's ordinary listing-search chat). question/reply are sent as plain
 * text (see ChatFeedbackCreate) rather than a message id: even a persisted
 * Talk-to-Your-Data turn doesn't hand its chat_messages id back to the
 * client today, and a plain text copy needs no plumbing to add later either.
 *
 * Locks once rated (no un-rate/re-rate in v1) — `onRate` is only called once
 * per turn, and the buttons below reflect whatever `rating` already is.
 */
function MessageFeedback({
  rating,
  onRate,
}: {
  rating: "up" | "down" | null | undefined;
  onRate: (rating: "up" | "down", reason?: string) => void;
}) {
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reason, setReason] = useState("");

  if (rating) {
    return (
      <div className="flex items-center gap-1 pt-0.5 text-muted-foreground">
        {rating === "up" ? (
          <ThumbsUp className="h-3 w-3 fill-current text-primary" />
        ) : (
          <ThumbsDown className="h-3 w-3 fill-current text-primary" />
        )}
        <span className="text-[10px]">Thanks for the feedback</span>
      </div>
    );
  }

  return (
    <div className="pt-0.5">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onRate("up")}
          className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          aria-label="Good response"
          title="Good response"
        >
          <ThumbsUp className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => setReasonOpen((v) => !v)}
          className={cn(
            "rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
            reasonOpen && "bg-accent text-foreground",
          )}
          aria-label="Bad response"
          title="Bad response"
        >
          <ThumbsDown className="h-3.5 w-3.5" />
        </button>
      </div>
      {reasonOpen && (
        <div className="mt-1.5 space-y-1.5 rounded-lg border border-border/70 bg-card/80 p-2">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="What went wrong? (optional)"
            rows={2}
            className="w-full resize-none rounded-md border border-border/60 bg-background px-2 py-1.5 text-xs outline-none focus-visible:ring-2 focus-visible:ring-ring/35"
          />
          <div className="flex items-center justify-end gap-1.5">
            <button
              type="button"
              onClick={() => onRate("down", reason.trim() || undefined)}
              className="rounded-md bg-accent px-2.5 py-1 text-[11px] font-semibold text-foreground transition-colors hover:bg-accent/70"
            >
              Submit
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Niva — the assistant panel, a full-height drawer down the right edge (the
 * shape every other platform's assistant uses, and the one DealNotesDrawer and
 * ListingAuditDrawer already use here). Gated by the platform-wide switch a
 * super admin owns (GET /chatbot/config), a signed-in user, AND a paid plan —
 * she is a member feature (see the Explorer/Operator columns on /choose-plan),
 * so a Basic buyer gets no launcher at all rather than one that only ever
 * answers "upgrade your plan".
 *
 * The switch is fetched, not read from a build-time env var: flipping it in the
 * super admin console has to reach users on their next page load, and a
 * VITE_ flag would need a frontend redeploy. The backend re-checks the same
 * switch on /chatbot/message, so a stale tab can't talk to a bot that is off. */
export default function ChatbotBubble() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  // Open/closed lives in NivaContext, not here: the "Ask Niva" button in the top
  // bar (Navigation.tsx) opens the same drawer from another branch of the tree.
  // `expanded` is wide mode — the same right-edge drawer, roughly doubled, for
  // reading listing cards two-up. Kept separate from `open` so narrowing returns
  // you to the docked drawer, not a closed one.
  const { open, expanded, available, openNiva, closeNiva, setExpanded, pendingPrompt, clearPendingPrompt } = useNiva();
  const setOpen = (next: boolean) => (next ? openNiva() : closeNiva());
  // ChatbotGate only mounts this with a user, and keys on user.id.
  const userId = user?.id ?? "";
  const [turns, setTurns] = useState<ChatTurn[]>(() => loadThread(userId));
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Seconds the current request has been running, for the waiting copy below.
  const [waited, setWaited] = useState(0);

  // ── Talk to Your Data — admin@portal.com only. Every piece of state below
  // is inert (never read, never set) for any other user; see isAdmin guards
  // at each call site. The backend re-checks admin@portal.com + role on every
  // request regardless of what this flag says — this only controls the UI.
  const isAdmin = isPortalAdmin(user);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [sessionsMenuOpen, setSessionsMenuOpen] = useState(false);
  const [sessionsList, setSessionsList] = useState<ChatSessionSummary[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  // The prompt library picker — a curated (question, SQL) list next to the
  // input. Selecting one skips SQL-generation entirely server-side (see
  // ChatRequest.prompt_library_id / route_and_handle_stream); this state is
  // just the picker UI itself.
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryItems, setLibraryItems] = useState<PromptLibraryItem[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  // Real server-reported pipeline stage (SSE, see api.postStream below) —
  // null until the first status event arrives, then overrides the
  // timer-based waiting copy every other user sees.
  const [progressStage, setProgressStage] = useState<string | null>(null);

  useEffect(() => {
    if (userId) saveThread(userId, turns);
  }, [userId, turns]);

  useEffect(() => {
    if (!loading) {
      setWaited(0);
      return;
    }
    const id = window.setInterval(() => setWaited((w) => w + 1), 1000);
    return () => window.clearInterval(id);
  }, [loading]);

  function startNewChat() {
    setTurns([]);
    setError(null);
    setInput("");
    saveThread(userId, []);
    // Admin-only: forget which saved session was active so the next message
    // starts a brand-new chat_sessions row server-side, instead of appending
    // to whatever was open. No-op (setting state that's already null) for
    // every other user.
    if (isAdmin) setActiveSessionId(null);
  }

  // ── Talk to Your Data history — admin@portal.com only ──────────────────
  async function toggleSessionsMenu() {
    const opening = !sessionsMenuOpen;
    setSessionsMenuOpen(opening);
    if (!opening) return;
    setSessionsLoading(true);
    try {
      const rows = await api.get<ChatSessionSummary[]>("/chatbot/sessions");
      setSessionsList(rows);
    } catch {
      // Menu just shows "No saved chats" — a failed history fetch shouldn't
      // block the admin from continuing to chat.
      setSessionsList([]);
    } finally {
      setSessionsLoading(false);
    }
  }

  async function loadSession(sessionId: string) {
    setSessionsMenuOpen(false);
    setError(null);
    try {
      const detail = await api.get<{
        id: string;
        title: string;
        messages: { id: string; role: string; content: string; metadata: Record<string, unknown>; created_at: string }[];
      }>(`/chatbot/sessions/${sessionId}`);
      const loaded: ChatTurn[] = detail.messages.map((m) => ({
        role: m.role === "assistant" ? "assistant" : "user",
        content: m.content,
        sql: (m.metadata?.sql as string | undefined) ?? null,
        dashboard: (m.metadata?.dashboard as TalkToDataDashboard | undefined) ?? null,
        // Reopening past feedback isn't shown (feedbackRating starts unset
        // here even if this turn was rated before) — v1 has no GET for a
        // message's existing rating, so it would silently look unrated and
        // let the admin submit a second one. Rating again just adds another
        // feedback row; it doesn't corrupt anything, so this is left as a
        // known gap rather than new machinery to close it.
        messageId: m.role === "assistant" ? m.id : null,
      }));
      setTurns(loaded);
      setActiveSessionId(detail.id);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't load that chat.");
    }
  }

  async function deleteSession(sessionId: string, e: React.MouseEvent) {
    // Stop the row's own onClick (loadSession) from also firing — the
    // delete button sits inside the row button, not beside it.
    e.stopPropagation();
    try {
      await api.delete(`/chatbot/sessions/${sessionId}`);
      setSessionsList((list) => list.filter((s) => s.id !== sessionId));
      // The chat currently open IS the one just deleted — there's nothing
      // left to show, so drop back to a clean New Chat rather than leaving
      // a transcript on screen for a session that no longer exists.
      if (sessionId === activeSessionId) {
        setTurns([]);
        setActiveSessionId(null);
        saveThread(userId, []);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't delete that chat.");
    }
  }
  // ── Talk to Your Data prompt library — admin@portal.com only ───────────
  async function toggleLibraryMenu() {
    const opening = !libraryOpen;
    setLibraryOpen(opening);
    if (!opening) return;
    setLibraryLoading(true);
    try {
      const rows = await api.get<PromptLibraryItem[]>("/chatbot/prompt-library");
      setLibraryItems(rows);
    } catch {
      // Menu just shows "No suggested questions" — a failed fetch shouldn't
      // block the admin from typing their own question instead.
      setLibraryItems([]);
    } finally {
      setLibraryLoading(false);
    }
  }

  function pickLibraryItem(item: PromptLibraryItem) {
    setLibraryOpen(false);
    void sendText(item.question, item.id);
  }

  // Thumbs up/down on one assistant turn (see MessageFeedback) — rates the
  // saved chat_messages row backing it, turn.messageId (never offered when
  // that's absent, see the call site's gate).
  async function rateTurn(turnIndex: number, rating: "up" | "down", reason?: string) {
    const turn = turns[turnIndex];
    if (!turn || turn.role !== "assistant" || !turn.messageId) return;
    try {
      await api.post("/chatbot/feedback", { message_id: turn.messageId, rating, reason });
      setTurns((prev) => prev.map((t, i) => (i === turnIndex ? { ...t, feedbackRating: rating } : t)));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't send feedback. Please try again.");
    }
  }

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [turns, loading]);

  // Escape narrows a widened drawer first, and only closes the docked one on a
  // second press — one keystroke should never cost a long conversation by
  // surprise. (Closing keeps the thread in sessionStorage either way.)
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (expanded) setExpanded(false);
      else setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, expanded]);

  // Open the gutter the drawer sits in. The panel is `position: fixed`, so it
  // occupies no layout space and simply covered the page — the shell has to be
  // told to give up the width (index.css keys off this attribute). Driven from
  // here because `open`/`expanded` live here, and torn down on unmount as well
  // as on close: a stuck attribute leaves a 28rem gutter with nothing in it.
  useEffect(() => {
    const root = document.documentElement;
    if (!open) {
      delete root.dataset.nivaDrawer;
      return;
    }
    root.dataset.nivaDrawer = expanded ? "wide" : "docked";
    return () => {
      delete root.dataset.nivaDrawer;
    };
  }, [open, expanded]);

  // A question handed in from another surface — the buyer dashboard's suggested
  // prompts, which open this drawer via askNiva() rather than running a second
  // chat of their own. Cleared as soon as it is taken so re-opening the drawer
  // doesn't re-ask it. The entitlement check is the same one the input uses;
  // an ineligible viewer gets the upgrade wall, not a silent 402.
  useEffect(() => {
    if (!pendingPrompt) return;
    clearPendingPrompt();
    if (!canUseNiva(user) || loading) return;
    void sendText(pendingPrompt);
    // sendText / loading are read at fire time; re-running on their identity
    // would re-ask the question.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPrompt]);

  // Where the viewer has parked the floating launcher, and whether they want it
  // at all. Declared above the guard below so the hook order stays fixed
  // whether or not she is available.
  const launcher = useDraggableLauncher<HTMLDivElement>();
  const [launcherHidden, setLauncherHidden] = useState(loadLauncherHidden);

  function hideLauncher() {
    setLauncherHidden(true);
    saveLauncherHidden(true);
    // The toast is the handover: dismissing a floating button people rely on
    // without saying where it went just moves the complaint.
    toast.toast(`${ASSISTANT_NAME} is hidden. Open her any time from "Ask ${ASSISTANT_NAME}" in the top bar.`);
  }

  function showLauncher() {
    setLauncherHidden(false);
    saveLauncherHidden(false);
  }

  // `available` (platform switch on + signed in + not a free/Basic buyer) is
  // computed once in NivaContext and shared with the top-bar button, so the
  // launcher here and that button can never disagree about whether she exists.
  if (!available || !user) return null;

  // Non-Operator subscribers still get the launcher — they see what she does and
  // an upgrade path, rather than the feature silently not existing for them.
  // The server refuses their messages too (routers/chatbot.py), so this panel
  // is the friendly face of that rule, not the rule itself.
  //
  // canUseNiva, not hasOperatorAccess: Explorer buyers get her free for their
  // first week (see lib/access.ts), and the same window gates /chatbot/message.
  const canUse = canUseNiva(user);
  // Non-null only DURING a live free week — Operator buyers never see the strip.
  const trialDaysLeft = hasOperatorAccess(user) ? null : nivaTrialDaysLeft(user);
  const trialExpired = isNivaTrialExpired(user);
  const firstName = (user.first_name || "").trim().split(" ")[0];

  /**
   * Open a listing from a chat card under the SAME entitlement rules as
   * Home.tsx's handleViewDetails — a raw link here would have been a paywall
   * bypass, since /listings/:id itself only guards on role, not plan or quota.
   *
   * Deliberately leans on the server for the parts that cost something:
   * POST /tiers/free-unlock/:id is the authority on quota (402), another
   * buyer's lock (409), and recording the unlock, so this can't drift out of
   * sync with Home's copy of the rules the way a re-implemented quota check
   * would. Only the two checks the server can't express as a 4xx on that
   * endpoint are done client-side: free-plan buyers (no tier at all → the
   * endpoint 403s, which isn't the message they need) and staff (no tier by
   * design, so the endpoint doesn't apply to them).
   */
  async function openListing(card: ChatListingCard) {
    const target = orgScopedPath(`/listings/${card.id}`, user!.org_slug);

    // Staff (admin / org_admin / seller / bl_advisor) hold no tier and are
    // never quota-limited — straight through, matching their REST access.
    if (isStaffUser(user)) {
      navigate(target);
      return;
    }

    // Free (Basic) buyers can't open listings at all — same redirect Home uses.
    if (isFreeBuyerUser(user)) {
      navigate(orgScopedPath("/choose-plan", user!.org_slug));
      return;
    }

    // Already unlocked by this viewer — no quota to spend, lock is irrelevant.
    if (card.is_unlocked) {
      navigate(target);
      return;
    }

    try {
      await api.post(`/tiers/free-unlock/${card.id}`, {});
      navigate(target);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        toast.error(err.message || "This listing is temporarily locked by another user.");
      } else if (err instanceof ApiError && err.status === 402) {
        // Free monthly quota exhausted — the paid-unlock page takes it from here.
        navigate(orgScopedPath(`/listings/${card.id}/unlock`, user!.org_slug));
      } else {
        toast.error(err instanceof ApiError ? err.message : "Unable to open listing. Please try again.");
      }
    }
  }

  async function sendText(raw: string, promptLibraryId?: string) {
    const text = raw.trim();
    if (!text || loading) return;
    setError(null);
    setInput("");
    const nextTurns: ChatTurn[] = [...turns, { role: "user", content: text }];
    setTurns(nextTurns);
    setLoading(true);
    setProgressStage(null);

    const requestMessages = nextTurns.map(({ role, content, listings }) => ({
      role,
      content,
      listing_refs: listings?.map(({ id, title }) => ({ id, title })),
    }));

    try {
      let res: ChatResponse;
      if (isAdmin) {
        // Server-Sent Events: real per-stage progress (see progressStage
        // below) as the NL-to-SQL pipeline actually runs, not a client-side
        // timer. Every non-admin user still hits plain POST /chatbot/message
        // below, completely unaffected by this branch.
        let streamed: ChatResponse | null = null;
        await api.postStream(
          "/chatbot/message/stream",
          { messages: requestMessages, session_id: activeSessionId, prompt_library_id: promptLibraryId ?? null },
          (event) => {
            if (event.type === "status" && typeof event.stage === "string") {
              setProgressStage(event.stage);
            } else if (event.type === "result") {
              streamed = {
                reply: String(event.reply ?? ""),
                listings: (event.listings as ChatListingCard[] | undefined) ?? [],
                sql: (event.sql as string | null | undefined) ?? null,
                dashboard: (event.dashboard as TalkToDataDashboard | null | undefined) ?? null,
                intent: (event.intent as string | null | undefined) ?? null,
                session_id: (event.session_id as string | null | undefined) ?? null,
                message_id: (event.message_id as string | null | undefined) ?? null,
              };
            } else if (event.type === "error") {
              throw new ApiError(502, typeof event.detail === "string" ? event.detail : "Something went wrong.");
            }
          },
        );
        if (!streamed) throw new ApiError(502, "The assistant is temporarily unavailable.");
        res = streamed;
      } else {
        res = await api.post<ChatResponse>("/chatbot/message", { messages: requestMessages });
      }
      setTurns([
        ...nextTurns,
        {
          role: "assistant",
          content: res.reply,
          listings: res.listings,
          requiresSubscription: res.requires_subscription,
          sql: res.sql,
          dashboard: res.dashboard,
          messageId: res.message_id,
        },
      ]);
      if (isAdmin && res.session_id) setActiveSessionId(res.session_id);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Something went wrong. Please try again.";
      setError(message);
      setTurns(nextTurns); // keep the user's message even though the reply failed
    } finally {
      setLoading(false);
      setProgressStage(null);
    }
  }

  return (
    <>
      {/* Backdrop — phones only, where the drawer genuinely covers the screen.
          On desktop the shell now shrinks to make room at either width, so
          nothing is hidden to dim; a scrim over content that is fully visible
          and still usable would just be lying about being modal. Widened mode
          narrows again via Escape or the header's narrow button. */}
      {open && (
        <div
          className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-[2px] sm:hidden"
          onClick={() => setOpen(false)}
          aria-hidden
        />
      )}

      {open && (
        <div
          role="dialog"
          aria-label={`${ASSISTANT_NAME} — ${ASSISTANT_TITLE}`}
          className={cn(
            "niva-drawer-in fixed inset-y-0 right-0 z-[51] flex w-full flex-col border-l border-border/70 bg-background shadow-2xl",
            // Same tokens the shell pads itself by (index.css) — one source of
            // truth, so the panel and the gutter it needs can't disagree.
            expanded ? "sm:max-w-[var(--niva-rail-w-wide)]" : "sm:max-w-[var(--niva-rail-w)]",
          )}
        >
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border/60 bg-card/60 px-4 py-3">
            <div className="flex min-w-0 items-center gap-3">
              <NivaAvatarBadge className="h-10 w-10" animated />
              <div className="min-w-0">
                <p className="text-sm font-bold leading-tight text-foreground">{ASSISTANT_NAME}</p>
                <p className="truncate text-[11px] text-muted-foreground">
                  {ASSISTANT_TITLE}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-0.5">
              {turns.length > 0 && (
                <button
                  type="button"
                  onClick={startNewChat}
                  className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                  aria-label="Start a new chat"
                  title="New chat"
                >
                  <PenSquare className="h-4 w-4" />
                </button>
              )}
              {/* Talk to Your Data's chat history — admin@portal.com only.
                  Niva's own listing-search chat still has no history menu at
                  all for every other account. A History icon, not a generic
                  "..." overflow kebab — this button does exactly one thing
                  (browse saved chats), so it earns its own recognizable icon
                  rather than reading as "more options". */}
              {isAdmin && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={toggleSessionsMenu}
                    className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                    aria-label="Browse chat history"
                    title="Chat history"
                  >
                    <History className="h-4 w-4" />
                  </button>
                  {sessionsMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-[52]" onClick={() => setSessionsMenuOpen(false)} />
                      <div className="absolute right-0 top-full z-[53] mt-1 w-64 overflow-hidden rounded-xl border border-border/70 bg-card shadow-xl">
                        <div className="max-h-72 overflow-y-auto py-1">
                          {sessionsLoading ? (
                            <p className="px-3 py-2 text-xs text-muted-foreground">Loading…</p>
                          ) : sessionsList.length === 0 ? (
                            <p className="px-3 py-2 text-xs text-muted-foreground">No saved chats yet.</p>
                          ) : (
                            sessionsList.map((s) => (
                              // A <div>, not a <button>, as the row wrapper —
                              // it holds two actual buttons (open / delete)
                              // side by side, and buttons can't nest.
                              <div key={s.id} className="group/row relative flex items-center">
                                <button
                                  type="button"
                                  onClick={() => loadSession(s.id)}
                                  className={cn(
                                    "block w-full min-w-0 py-2 pl-3 pr-8 text-left transition-colors hover:bg-accent",
                                    s.id === activeSessionId && "bg-accent/60 font-medium",
                                  )}
                                  title={s.title}
                                >
                                  <p className="truncate text-xs text-foreground">{s.title}</p>
                                  <p className="mt-0.5 text-[10px] text-muted-foreground">{timeAgo(s.updated_at)}</p>
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => deleteSession(s.id, e)}
                                  className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive focus-visible:opacity-100 group-hover/row:opacity-100"
                                  aria-label={`Delete "${s.title}"`}
                                  title="Delete this chat"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
              {/* The way back from the launcher's X. Only shown once it has been
                  dismissed, so it's an undo rather than one more control to
                  read past — and it lives at every width, because the phone is
                  where she gets dismissed. */}
              {launcherHidden && (
                <button
                  type="button"
                  onClick={showLauncher}
                  className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                  aria-label={`Show the floating Ask ${ASSISTANT_NAME} button`}
                  title="Show the floating button"
                >
                  <Pin className="h-4 w-4" />
                </button>
              )}
              {/* Widening is a desktop affordance — the drawer is already
                  full-bleed on a phone, where this button would do nothing. */}
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="hidden rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground sm:block"
                aria-label={expanded ? `Narrow ${ASSISTANT_NAME}` : `Widen ${ASSISTANT_NAME}`}
                title={expanded ? "Narrow" : "Widen"}
              >
                {expanded ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                aria-label={`Close ${ASSISTANT_NAME}`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Her recorded introduction, at the top of the panel — shown to
              non-Operator viewers too, where it is the clearest pitch for what
              they'd be upgrading to. It folds itself away once played. */}
          <NivaIntro />

          {!canUse ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-8 text-center">
              <NivaAvatar className="h-20 w-20" animated />
              <p className="text-sm font-semibold text-foreground">
                {trialExpired
                  ? `Your free week with ${ASSISTANT_NAME} has ended`
                  : `${ASSISTANT_NAME} is available on the Operator plan`}
              </p>
              <p className="max-w-[19rem] text-xs leading-relaxed text-muted-foreground">
                {/* A buyer who just spent a week using her doesn't need the pitch —
                    they need to know what they lost and how to get it back. */}
                {trialExpired
                  ? "She stays on the Operator plan, along with unlimited unlocks, deal locks and 24-hour early access. Upgrade to keep searching with her."
                  : "She searches the whole marketplace in plain English — try “profitable auto shops in Texas under $500k” — and answers questions about any listing she finds."}
              </p>
              <Button
                variant="premium"
                size="sm"
                onClick={() => navigate(orgScopedPath(upgradeCtaPath(user, "Operator"), user.org_slug))}
              >
                Upgrade to Operator
              </Button>
            </div>
          ) : (
          <>
          {/* The free week, counted down. Shown only to an Explorer buyer inside
              the window (never to Operator or staff): access that disappears
              without warning reads as a bug, and this is also the upgrade pitch
              landing while she is demonstrably useful to them. */}
          {trialDaysLeft !== null && (
            <div className="flex items-center justify-between gap-2 border-b border-primary/25 bg-primary/10 px-4 py-2">
              <p className="text-[11px] leading-tight text-foreground">
                <span className="font-semibold">
                  {trialDaysLeft === 1 ? "Last day" : `${trialDaysLeft} days left`}
                </span>{" "}
                <span className="text-muted-foreground">
                  of your free week with {ASSISTANT_NAME}
                </span>
              </p>
              <button
                type="button"
                onClick={() => navigate(orgScopedPath(upgradeCtaPath(user, "Operator"), user.org_slug))}
                className="shrink-0 text-[11px] font-semibold text-primary underline-offset-2 hover:underline"
              >
                Keep her
              </button>
            </div>
          )}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            {turns.length === 0 && (
              // The greeting is rendered, not stored as a real first turn: it must
              // never end up in the history posted to /chatbot/message, where it
              // would read as something the model already said.
              <div className="flex flex-col items-center px-2 py-6 text-center">
                <span className="relative flex items-center justify-center">
                  <span className="niva-halo absolute inset-0 rounded-full bg-primary/25 blur-md" aria-hidden />
                  <NivaAvatar className="relative h-24 w-24" animated />
                </span>
                <p className="mt-4 font-display text-2xl text-foreground">
                  Hi{firstName ? ` ${firstName}` : " there"}, I&apos;m {ASSISTANT_NAME}
                </p>
                <p className="mt-1 text-sm font-semibold text-foreground">
                  How can I help you today?
                </p>
                <p className="mt-1.5 max-w-[22rem] text-xs leading-relaxed text-muted-foreground">
                  Describe the business you&apos;re looking for and I&apos;ll search the
                  marketplace — or ask me anything about a listing I find.
                </p>
                <div className="mt-5 w-full space-y-2 text-left">
                  <p className="px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Try asking
                  </p>
                  {SUGGESTIONS.map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => sendText(q)}
                      className="group flex w-full items-center justify-between gap-2 rounded-xl border border-border/70 bg-card/70 px-3.5 py-2.5 text-left text-xs text-muted-foreground transition-colors hover:border-primary/50 hover:bg-card hover:text-foreground"
                    >
                      <span>{q}</span>
                      <ArrowRight className="h-3.5 w-3.5 shrink-0 text-primary opacity-0 transition-opacity group-hover:opacity-100" />
                    </button>
                  ))}
                </div>
              </div>
            )}
            {turns.map((turn, i) => (
              <div
                key={i}
                className={cn(
                  "flex items-start gap-2",
                  turn.role === "user" ? "justify-end" : "justify-start",
                )}
              >
                {/* Her face on every reply, the way an avatar repeats down any
                    chat thread — one per turn, not one per bubble. */}
                {turn.role === "assistant" && <NivaAvatar className="mt-0.5 h-7 w-7" />}
                <div
                  className={cn(
                    "space-y-2",
                    turn.role === "user" ? "max-w-[85%]" : "max-w-[calc(92%-2.25rem)]",
                    // A 700px-wide bubble is an unreadable line length.
                    expanded && "max-w-[min(85%,42rem)]",
                  )}
                >
                  <div
                    className={
                      turn.role === "user"
                        ? "rounded-2xl rounded-br-sm bg-gold-gradient text-primary-foreground px-3.5 py-2 text-sm whitespace-pre-wrap"
                        : "rounded-2xl rounded-bl-sm bg-accent/60 text-foreground px-3.5 py-2 text-sm"
                    }
                  >
                    {turn.role === "assistant" ? (
                      <ReactMarkdown components={MARKDOWN_COMPONENTS}>{turn.content}</ReactMarkdown>
                    ) : (
                      turn.content
                    )}
                  </div>
                  {turn.listings && turn.listings.length > 0 && (
                    <div className={cn("grid grid-cols-1 gap-2", expanded && "sm:grid-cols-2")}>
                      {turn.listings.map((card) => (
                        <ListingCardView key={card.id} card={card} onOpen={openListing} />
                      ))}
                    </div>
                  )}
                  {/* Talk to Your Data — generated SQL + result table. Only
                      ever present on an admin's own turns; absent for every
                      other user's responses. */}
                  {(turn.sql || turn.dashboard) && (
                    <TalkToDataResult sql={turn.sql ?? null} dashboard={turn.dashboard ?? null} />
                  )}
                  {turn.requiresSubscription && (
                    <Button
                      variant="premium"
                      size="sm"
                      onClick={() => navigate(orgScopedPath(upgradeCtaPath(user, "Operator"), user.org_slug))}
                    >
                      Upgrade to Operator
                    </Button>
                  )}
                  {/* Only when this turn actually got a chat_messages row —
                      set exclusively for genuine Talk-to-Data turns, never
                      for Niva's ordinary listing-search replies (see
                      _TALK_TO_DATA_INTENTS server-side). Gating on whether a
                      message id exists, not on "is this admin@portal.com",
                      means this needs no changes once Talk to Your Data
                      opens up to more users. */}
                  {turn.role === "assistant" && turn.messageId && (
                    <MessageFeedback
                      rating={turn.feedbackRating}
                      onRate={(rating, reason) => rateTurn(i, rating, reason)}
                    />
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex items-start justify-start gap-2">
                <NivaAvatar className="mt-0.5 h-7 w-7" />
                <div className="flex items-center gap-2 rounded-2xl rounded-bl-sm bg-accent/60 px-3.5 py-2.5 text-sm text-muted-foreground">
                  <span className="flex gap-1" aria-hidden>
                    {[0, 150, 300].map((delay) => (
                      <span
                        key={delay}
                        className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary/70"
                        style={{ animationDelay: `${delay}ms` }}
                      />
                    ))}
                  </span>
                  {/* Admin: real server-reported stage from the SSE stream
                      (see progressStage / api.postStream) — one event per
                      actual pipeline step (understanding the question,
                      generating SQL, running it, validating, summarizing),
                      not a fake timer. Falls back to "Thinking…" only for
                      the gap before the first event arrives. Every other
                      user still gets the plain REST /chatbot/message call
                      and its timer-based copy, unchanged. */}
                  {isAdmin
                    ? progressStage ?? "Thinking…"
                    : waited < 4
                    ? "Thinking…"
                    : waited < 10
                    ? "Searching the marketplace…"
                    : "Still working…"}
                </div>
              </div>
            )}
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>

          {/* Taller than a stock input row on purpose: this is the one control
              in the panel the user actually types into, and at the app's 0.75
              base zoom a 40px row renders at 30 real pixels — a cramped target
              pinned to the bottom edge of the screen. The mic button is sized
              to match from its own file. */}
          <form
            className="flex shrink-0 items-center gap-2 border-t border-border/60 bg-card/40 p-4"
            onSubmit={(e) => {
              e.preventDefault();
              sendText(input);
            }}
          >
            {/* Talk to Your Data's prompt library — admin@portal.com only.
                A single icon, not a row of chips: the library can grow past
                what fits as chips, and this costs no more width than the mic/
                send buttons already sitting in this row. Opens UPWARD
                (bottom-full), unlike the History popover above, since this
                button sits at the very bottom edge of the panel. */}
            {isAdmin && (
              <div className="relative shrink-0">
                <button
                  type="button"
                  onClick={toggleLibraryMenu}
                  className="flex h-12 w-12 items-center justify-center rounded-xl border border-border/70 bg-card text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                  disabled={loading}
                  aria-label="Suggested questions"
                  title="Suggested questions"
                >
                  <ListChecks className="h-5 w-5" />
                </button>
                {libraryOpen && (
                  <>
                    <div className="fixed inset-0 z-[52]" onClick={() => setLibraryOpen(false)} />
                    <div className="absolute bottom-full left-0 z-[53] mb-1 w-72 overflow-hidden rounded-xl border border-border/70 bg-card shadow-xl">
                      <p className="border-b border-border/60 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Suggested questions
                      </p>
                      <div className="max-h-72 overflow-y-auto py-1">
                        {libraryLoading ? (
                          <p className="px-3 py-2 text-xs text-muted-foreground">Loading…</p>
                        ) : libraryItems.length === 0 ? (
                          <p className="px-3 py-2 text-xs text-muted-foreground">No suggested questions yet.</p>
                        ) : (
                          libraryItems.map((item) => (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => pickLibraryItem(item)}
                              className="block w-full px-3 py-2 text-left text-xs text-foreground transition-colors hover:bg-accent"
                            >
                              {item.question}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={`Ask ${ASSISTANT_NAME} about a listing…`}
              className="h-12 min-w-0 flex-1 rounded-xl border border-border/70 bg-card px-3.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/35"
              disabled={loading}
            />
            <VoiceSearch onSearch={sendText} disabled={loading} iconOnly />
            <Button
              type="submit"
              size="icon"
              className="h-12 w-12 shrink-0"
              disabled={loading || !input.trim()}
              aria-label="Send"
            >
              <Send className="h-5 w-5" />
            </Button>
          </form>
          </>
          )}
        </div>
      )}

      {/* The launcher hides while the drawer is open, where it would sit on top
          of the panel's own close button.

          Draggable, because bottom-right is also where the listing filter
          drawer keeps "Apply filters": parked here she covered the button and
          buyers had no way past her. `dragProps` supplies the transform and
          swallows the click that ends a drag.

          Dismissible too (TKT-00119), because dragging only ever relocates the
          problem on a phone: a fixed launcher follows the page however far it
          scrolls, and 340-odd points of screen has nowhere that is out of the
          way. The X is the honest answer, and the top bar keeps her reachable.

          A wrapper div, not the button itself, carries the drag: the dismiss
          control has to be a real button and buttons can't nest. */}
      {!open && !launcherHidden && (
        <div
          ref={launcher.ref}
          {...launcher.dragProps}
          className={cn(
            // niva-launcher: lifted above the mobile buyer tab bar (index.css).
            "niva-launcher fixed bottom-5 right-5 z-50 flex select-none items-center",
            launcher.dragging ? "cursor-grabbing" : "cursor-grab",
          )}
        >
          <button
            type="button"
            onClick={() => setOpen(true)}
            className={cn(
              // Icon-only on a phone, the same trade the top bar's own Ask Niva
              // button makes: at this width her name costs more screen than it
              // earns, and the pill was most of the bottom edge.
              "flex h-14 items-center gap-2.5 rounded-full bg-gold-gradient px-1.5 text-primary-foreground shadow-xl hover:brightness-[1.06] sm:pr-5",
              // No transition mid-drag or the pill eases along behind the cursor.
              launcher.dragging ? "shadow-2xl" : "transition-all",
            )}
            title={`Open ${ASSISTANT_NAME} — drag to move her, or X to hide her`}
            aria-label={`Open ${ASSISTANT_NAME} — ${ASSISTANT_TITLE}`}
          >
            {/* Dark ring, not the default gold one — the launcher pill is itself
                gold, and gold-on-gold left her with no edge at all. */}
            <NivaAvatarBadge
              className="h-11 w-11 ring-primary-foreground/30"
              dotClassName="border-[#c2a24f]"
              animated
            />
            <span className="hidden flex-col items-start leading-tight sm:flex">
              <span className="text-sm font-bold">Ask {ASSISTANT_NAME}</span>
              <span className="inline-flex items-center gap-1 text-[10px] font-medium opacity-80">
                <Sparkles className="h-2.5 w-2.5" /> {ASSISTANT_TITLE}
              </span>
            </span>
          </button>
          {/* Card, not gold: a gold chip on a gold pill is invisible, and this
              pair of tokens flips correctly in both themes. Larger touch target
              on a phone, which is where it will actually be used — everything
              renders at 75% there (--app-zoom), so h-6 would land at 18 real px.
              Its own onPointerDown stops the wrapper starting a drag gesture,
              so a tap on the X is only ever a tap. */}
          <button
            type="button"
            onClick={hideLauncher}
            onPointerDown={(e) => e.stopPropagation()}
            className="absolute -right-1.5 -top-1.5 inline-flex h-8 w-8 items-center justify-center rounded-full border border-border/70 bg-card text-muted-foreground shadow-md transition-colors hover:bg-accent hover:text-foreground sm:h-7 sm:w-7"
            title={`Hide the floating button — Ask ${ASSISTANT_NAME} stays in the top bar`}
            aria-label={`Hide the floating ${ASSISTANT_NAME} button`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </>
  );
}
